import { Markdown } from '@/components/ui/markdown'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { cn, copy } from '@/lib/utils'
import { ChevronsRightIcon, CopyIcon } from 'lucide-react'
import React from 'react'
import { MessageCode } from '../message-types'

type MarkdownTask = { type: 'markdown'; content: string; markdownContent?: string }
type CodeTask = { type: 'code'; content: string; language?: string }
type Task = MarkdownTask | CodeTask

const PlanStepTaskItems: React.FC<{
  tasks: Task[]
  animateIndex?: number | null
  isCompact?: boolean
}> = ({ tasks, animateIndex = null, isCompact }) =>
  tasks.map((task, taskIndex) => {
    if (task.type === 'code') {
      const { content, language } = task
      return (
        <div
          className={cn('w-full snap-start items-start first:pt-0', isCompact && 'pt-2')}
          key={taskIndex}
        >
          {!isCompact && (
            <div className='sticky -top-8 right-0 z-10 h-0 overflow-visible'>
              <TooltipIconButton
                unstyled
                size='xs'
                titleString='Copy code'
                className='relative top-2 ml-auto mr-1.5'
                onClick={() => {
                  copy({
                    text: content,
                    errorMessage: 'Failed to copy code',
                    successMessage: 'Copied to clipboard',
                  })
                }}
              >
                <CopyIcon className='size-4' />
              </TooltipIconButton>
            </div>
          )}
          <div className='flex w-full items-start gap-1.5'>
            <ChevronsRightIcon className='size-[14px] shrink-0 opacity-45' strokeWidth={2} />
            <MessageCode
              isCompact={isCompact}
              codeString={content}
              language={language}
              renderIcons={() => null}
              className='h-full w-full !overflow-x-auto pt-0 [&>pre]:!pt-4'
            />
          </div>
        </div>
      )
    }

    return (
      <div
        className={cn(
          isCompact && 'items-center pt-2',
          'flex w-full shrink-0 translate-x-0 snap-start items-start gap-1.5 overflow-hidden opacity-100 transition-[max-height,opacity,transform] ease-in-out first:pt-0',
          animateIndex !== null && animateIndex < taskIndex && '-translate-x-full opacity-0',
        )}
        key={taskIndex}
      >
        <ChevronsRightIcon className='mt-1 size-[14px] shrink-0 opacity-45' strokeWidth={2} />
        {isCompact ? (
          <span className='line-clamp-1 h-5 whitespace-normal break-all text-sm font-light italic text-gray-500'>
            {task.content || task.markdownContent}
          </span>
        ) : (
          <div className='w-full items-start'>
            <Markdown className='w-full'>{task.markdownContent || task.content}</Markdown>
          </div>
        )}
      </div>
    )
  })

export { PlanStepTaskItems, type Task }
